import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Activity, User, Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { BASE_URL } from './config';

export default function Register() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ valid: false, message: '' });
  const [showPassword, setShowPassword] = useState(false);

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return { valid: false, message: 'Password must be at least 8 characters' };
    if (!/[a-z]/.test(pwd)) return { valid: false, message: 'Password must contain lowercase letters' };
    if (!/[A-Z]/.test(pwd)) return { valid: false, message: 'Password must contain uppercase letters' };
    if (!/[0-9]/.test(pwd)) return { valid: false, message: 'Password must contain numbers' };
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return { valid: false, message: 'Password must contain special characters' };
    return { valid: true, message: 'Strong password' };
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordStrength(validatePassword(pwd));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!passwordStrength.valid) {
      setError(passwordStrength.message);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BASE_URL}/api/superadmin/register/`, { username, email, password, role });
      setError('');
      navigate('/');
    } catch (err) {
      setError('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    // { value: '5', label: 'Admin' },
    { value: '4', label: 'Hospital' },
    { value: '3', label: 'Clinic' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-3">
            <img src="/logo-footer.png" alt="ClinicalAssist" className="h-20" />
          </div>
          {/* <h1 className="text-2xl font-semibold text-slate-800">ClinicalAssist</h1> */}
          <p className="text-sm text-slate-600 mt-1">Healthcare Management System</p>
        </div>

        <div className="bg-white rounded border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-5">Register</h2>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Username</label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type="text"
                  placeholder="Enter username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className={`w-full pl-9 pr-10 py-2 border rounded focus:ring-1 outline-none text-sm ${
                    password && !passwordStrength.valid 
                      ? 'border-red-300 focus:ring-red-500' 
                      : password && passwordStrength.valid
                      ? 'border-green-300 focus:ring-green-500'
                      : 'border-slate-300 focus:ring-primary-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {password && (
                <div className="mt-2 space-y-0.5">
                  <p className={`text-xs ${password.length >= 8 ? 'text-green-600' : 'text-slate-500'}`}>
                    {password.length >= 8 ? '✓' : '○'} At least 8 characters
                  </p>
                  <p className={`text-xs ${/[a-z]/.test(password) ? 'text-green-600' : 'text-slate-500'}`}>
                    {/[a-z]/.test(password) ? '✓' : '○'} Lowercase letter
                  </p>
                  <p className={`text-xs ${/[A-Z]/.test(password) ? 'text-green-600' : 'text-slate-500'}`}>
                    {/[A-Z]/.test(password) ? '✓' : '○'} Uppercase letter
                  </p>
                  <p className={`text-xs ${/[0-9]/.test(password) ? 'text-green-600' : 'text-slate-500'}`}>
                    {/[0-9]/.test(password) ? '✓' : '○'} Number
                  </p>
                  <p className={`text-xs ${/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-600' : 'text-slate-500'}`}>
                    {/[!@#$%^&*(),.?":{}|<>]/.test(password) ? '✓' : '○'} Special character
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Select Role</label>
              <select
                value={role}
                onChange={e => setRole(e.target.value)}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
              >
                <option value="" disabled>Choose your role</option>
                {roles.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded transition-colors disabled:opacity-50 text-sm"
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>

          <div className="mt-5 text-center">
            <p className="text-slate-600 text-xs">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
